/** Request detail: review, edit, attachments and conversion. */
import { useEffect, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type {
  AttachmentResponse,
  OfferResponse,
  RequestResponse,
  RequestStatus,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Textarea } from "@primereact/ui/textarea";
import { apiDeleteBody, apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { useMe, usePermissions } from "../../lib/auth.js";
import { Field } from "../../components/ui/Field.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Modal } from "../../components/ui/controls.js";
import {
  ErrorPanel,
  LoadingPanel,
  MutationError,
  formatDate,
  formatMoney,
} from "../shared.js";
import {
  DocumentPage,
  Notice,
  Panel,
  RelatedLinks,
  StatusTimeline,
  useCustomer,
} from "../commercial/document.js";
import { mapsUrl } from "../customers/routes.js";
import { useCategories } from "./request-common.js";
import {
  type PendingPhoto,
  PhotoPicker,
  type RequestDraft,
  RequestFields,
  draftFromRequest,
  draftValid,
  requestPayload,
  uploadPhotos,
} from "./RequestForm.js";

export function AttachmentGallery({
  request,
  canEdit,
}: {
  request: RequestResponse;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const me = useMe();
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const [viewer, setViewer] = useState<AttachmentResponse | null>(null);
  const upload = useMutation({
    mutationFn: () =>
      uploadPhotos(request.id, pending, (key, progress, error) =>
        setPending((current) =>
          current.map((photo) =>
            photo.key === key ? { ...photo, progress, error } : photo,
          ),
        ),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["request", request.id],
      });
      setPending((current) => current.filter((photo) => photo.error));
    },
  });
  const remove = useMutation({
    mutationFn: (attachmentId: string) =>
      apiDeleteBody(`/requests/${request.id}/attachments/${attachmentId}`, {}),
    onSuccess: () => {
      setViewer(null);
      void queryClient.invalidateQueries({ queryKey: ["request", request.id] });
    },
  });
  return (
    <Panel title={`${t("request.photos")} (${request.attachments.length})`}>
      {request.attachments.length ? (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {request.attachments.map((attachment) => (
            <li key={attachment.id}>
              <button
                type="button"
                onClick={() =>
                  attachment.thumbnailUrl
                    ? setViewer(attachment)
                    : window.open(attachment.downloadUrl, "_blank")
                }
                className="block aspect-square w-full overflow-hidden rounded-lg border border-app-border bg-app-background"
              >
                {attachment.thumbnailUrl ? (
                  <img
                    src={attachment.thumbnailUrl}
                    alt={attachment.caption ?? attachment.fileName}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full flex-col items-center justify-center gap-1 p-2 text-xs text-app-muted">
                    <i className="pi pi-file-pdf text-2xl" aria-hidden="true" />
                    <span className="line-clamp-2 break-all">
                      {attachment.fileName}
                    </span>
                  </span>
                )}
              </button>
              {attachment.caption ? (
                <p className="mt-1 truncate text-xs text-app-muted">
                  {attachment.caption}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : pending.length === 0 ? (
        <p className="text-sm text-app-muted">{t("request.noPhotos")}</p>
      ) : null}
      {canEdit && has("files.upload") ? (
        <div className="mt-4 space-y-3">
          <PhotoPicker photos={pending} onChange={setPending} />
          {pending.length ? (
            <Button
              loading={upload.isPending || undefined}
              icon="pi pi-upload"
              onClick={() => upload.mutate()}
            >
              {t("request.uploadPhotos", { count: pending.length })}
            </Button>
          ) : null}
        </div>
      ) : null}
      <Modal
        open={viewer !== null}
        onClose={() => setViewer(null)}
        wide
        title={viewer?.caption || viewer?.fileName || ""}
        footer={
          viewer ? (
            <>
              {(viewer.uploadedById === me.data?.user.id && canEdit) ||
              has("files.delete") ? (
                <Button
                  variant="text"
                  severity="danger"
                  icon="pi pi-trash"
                  loading={remove.isPending || undefined}
                  onClick={() =>
                    window.confirm(t("request.deletePhotoConfirm")) &&
                    remove.mutate(viewer.id)
                  }
                >
                  {t("common.delete")}
                </Button>
              ) : null}
              <Button
                as="a"
                href={`${viewer.downloadUrl}?download=1`}
                variant="outlined"
                icon="pi pi-download"
              >
                {t("common.download")}
              </Button>
            </>
          ) : null
        }
      >
        {viewer ? (
          <img
            src={viewer.downloadUrl}
            alt={viewer.caption ?? viewer.fileName}
            className="max-h-[70vh] w-full rounded-lg object-contain"
          />
        ) : null}
      </Modal>
    </Panel>
  );
}

export function RequestEditPanel({
  request,
  onDone,
}: {
  request: RequestResponse;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const categories = useCategories();
  const customer = useCustomer(request.customer.id);
  const [draft, setDraft] = useState<RequestDraft | null>(null);
  useEffect(() => {
    if (categories.data && !draft)
      setDraft(draftFromRequest(request, categories.data));
  }, [categories.data, draft, request]);
  const save = useMutation({
    mutationFn: () => {
      const { customerId: _customerId, ...payload } = requestPayload(
        draft!,
        customer.data!,
        has("finance.view"),
        has("requests.assign"),
      );
      return apiPatch<{ data: RequestResponse }>(`/requests/${request.id}`, {
        ...payload,
        version: request.version,
      });
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["request", request.id], response.data);
      void queryClient.invalidateQueries({ queryKey: ["requests"] });
      onDone();
    },
  });
  if (!draft || !customer.data) return <LoadingPanel />;
  return (
    <div className="space-y-4">
      <RequestFields
        draft={draft}
        onChange={setDraft}
        customer={customer.data}
        firstStep={1}
      />
      <MutationError error={save.error} />
      <div className="flex justify-end gap-2">
        <Button variant="text" severity="secondary" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button
          loading={save.isPending || undefined}
          disabled={!draftValid(draft, has("finance.view"))}
          onClick={() => save.mutate()}
        >
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
}

export function RequestDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const me = useMe();
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState<"declined" | "cancelled" | null>(null);
  const [reason, setReason] = useState("");
  const request = useQuery({
    queryKey: ["request", id],
    queryFn: () =>
      apiGet<{ data: RequestResponse }>(`/requests/${id}`).then(
        (response) => response.data,
      ),
  });
  const apply = (response: { data: RequestResponse }) => {
    queryClient.setQueryData(["request", id], response.data);
    void queryClient.invalidateQueries({ queryKey: ["requests"] });
  };
  const transition = useMutation({
    mutationFn: (input: { status: RequestStatus; reason?: string }) =>
      apiPost<{ data: RequestResponse }>(`/requests/${id}/status`, {
        version: request.data!.version,
        ...input,
      }),
    onSuccess: (response) => {
      apply(response);
      setClosing(null);
      setReason("");
    },
  });
  const createOffer = useMutation({
    mutationFn: () =>
      apiPost<{ data: OfferResponse }>("/offers", {
        customerId: request.data!.customer.id,
        requestId: id,
        title: request.data!.shortDescription,
      }),
    onSuccess: (response) => navigate(`/offers/${response.data.id}`),
  });
  const remove = useMutation({
    mutationFn: () =>
      apiDeleteBody(`/requests/${id}`, { version: request.data!.version }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["requests"] });
      navigate("/requests");
    },
  });

  if (request.isLoading) return <LoadingPanel />;
  if (request.error || !request.data)
    return <ErrorPanel error={request.error} />;
  const data = request.data;
  const mine =
    data.createdById === me.data?.user.id ||
    data.assignedUsers.some((user) => user.id === me.data?.user.id);
  const canEdit =
    ["draft", "submitted", "in_review"].includes(data.status) &&
    (has("requests.edit_all") || (has("requests.edit_own") && mine));
  const canChange = has("requests.change_status");
  const closed = ["converted", "declined", "cancelled"].includes(data.status);
  const finance = has("finance.view");

  const actions = (
    <>
      {data.status === "draft" && canEdit ? (
        <Button
          icon="pi pi-send"
          loading={transition.isPending || undefined}
          onClick={() => transition.mutate({ status: "submitted" })}
        >
          {t("request.submitToOffice")}
        </Button>
      ) : null}
      {data.status === "submitted" && canChange ? (
        <Button
          variant="outlined"
          icon="pi pi-eye"
          loading={transition.isPending || undefined}
          onClick={() => transition.mutate({ status: "in_review" })}
        >
          {t("request.startReview")}
        </Button>
      ) : null}
      {!closed && data.status !== "draft" && finance && has("offers.create") ? (
        <Button
          icon="pi pi-file"
          loading={createOffer.isPending || undefined}
          onClick={() => createOffer.mutate()}
        >
          {t("request.createOffer")}
        </Button>
      ) : null}
      {!closed &&
      data.status !== "draft" &&
      finance &&
      has("invoices.create") &&
      data.pricingPreference !== "offer_required" ? (
        <Button
          variant="outlined"
          icon="pi pi-receipt"
          onClick={() => navigate(`/invoices/new?requestId=${data.id}`)}
        >
          {t("request.invoiceDirectly")}
        </Button>
      ) : null}
      {closed && canChange ? (
        <Button
          variant="outlined"
          severity="secondary"
          icon="pi pi-replay"
          loading={transition.isPending || undefined}
          onClick={() => transition.mutate({ status: "in_review" })}
        >
          {t("request.reopen")}
        </Button>
      ) : null}
    </>
  );

  return (
    <DocumentPage
      back={{ to: "/requests", label: t("request.plural") }}
      number={data.number}
      status={data.status}
      title={data.shortDescription}
      meta={
        <>
          <Link
            to={`/customers/${data.customer.id}`}
            className="hover:underline"
          >
            {data.customer.displayName}
          </Link>
          {data.priority !== "normal" ? (
            <>
              {" · "}
              <StatusBadge status={data.priority} />
            </>
          ) : null}
        </>
      }
      actions={actions}
      aside={
        <>
          <Panel title={t("common.customer")}>
            <div className="space-y-1 text-sm">
              <Link
                to={`/customers/${data.customer.id}`}
                className="font-medium hover:underline"
              >
                {data.customer.displayName}
              </Link>
              {data.customer.phone ? (
                <a
                  href={`tel:${data.customer.phone}`}
                  className="flex items-center gap-2 text-brand"
                >
                  <i className="pi pi-phone" aria-hidden="true" />
                  {data.customer.phone}
                </a>
              ) : null}
              {data.customer.email ? (
                <a
                  href={`mailto:${data.customer.email}`}
                  className="flex items-center gap-2 break-all text-brand"
                >
                  <i className="pi pi-envelope" aria-hidden="true" />
                  {data.customer.email}
                </a>
              ) : null}
            </div>
          </Panel>
          {data.siteAddress ? (
            <Panel title={t("request.site")}>
              <a
                href={mapsUrl(data.siteAddress)}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-2 text-sm hover:underline"
              >
                <i className="pi pi-map-marker mt-0.5" aria-hidden="true" />
                <span>
                  {data.siteAddress.line1}
                  <br />
                  {data.siteAddress.postalCode} {data.siteAddress.city}
                </span>
              </a>
            </Panel>
          ) : null}
          <Panel title={t("request.assignees")}>
            {data.assignedUsers.length ? (
              <ul className="text-sm">
                {data.assignedUsers.map((user) => (
                  <li key={user.id}>
                    {user.firstName} {user.lastName}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-app-muted">
                {t("request.unassigned")}
              </p>
            )}
          </Panel>
          {finance ? (
            <Panel title={t("doc.related")}>
              <RelatedLinks
                items={[
                  ...(data.project
                    ? [
                        {
                          label: t("project.singular"),
                          number: data.project.number,
                          to: `/projects/${data.project.id}`,
                        },
                      ]
                    : []),
                  ...data.offers.map((offer) => ({
                    label: t("offer.singular"),
                    number: offer.displayNumber,
                    status: offer.status,
                    to: `/offers/${offer.id}`,
                  })),
                  ...data.invoices.map((invoice) => ({
                    label: t("invoice.singular"),
                    number: invoice.number ?? t("invoice.draftNumber"),
                    status: invoice.status,
                    to: `/invoices/${invoice.id}`,
                  })),
                ]}
              />
            </Panel>
          ) : null}
          <StatusTimeline
            entityType="request"
            id={data.id}
            version={data.version}
          />
          <p className="px-1 text-xs text-app-muted">
            {t("doc.createdBy", {
              name: data.createdBy.name,
              date: formatDate(data.createdAt),
            })}
          </p>
        </>
      }
    >
      <MutationError
        error={transition.error ?? createOffer.error ?? remove.error}
      />
      {data.statusReason ? (
        <Notice tone={data.status === "declined" ? "danger" : "warning"}>
          {t(`request.closedReason.${data.status}`, {
            reason: data.statusReason,
          })}
        </Notice>
      ) : null}
      {data.status === "draft" && mine ? (
        <Notice>{t("request.draftNotice")}</Notice>
      ) : null}
      {editing ? (
        <RequestEditPanel request={data} onDone={() => setEditing(false)} />
      ) : (
        <Panel
          title={t("request.details")}
          actions={
            canEdit ? (
              <Button
                size="small"
                variant="text"
                icon="pi pi-pencil"
                onClick={() => setEditing(true)}
              >
                {t("common.edit")}
              </Button>
            ) : null
          }
        >
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-app-muted">
                {t("request.category")}
              </dt>
              <dd>
                {[data.categoryName, data.subcategoryName]
                  .filter(Boolean)
                  .join(" › ") || "—"}
              </dd>
            </div>
            {data.description ? (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-app-muted">
                  {t("request.details")}
                </dt>
                <dd className="whitespace-pre-line">{data.description}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-app-muted">
                {t("request.when")}
              </dt>
              <dd>
                {data.schedulingPreference === "asap"
                  ? t("request.asap")
                  : [
                      formatDate(data.requestedDate),
                      data.requestedTimeStart
                        ? `${data.requestedTimeStart}–${data.requestedTimeEnd ?? ""}`
                        : null,
                      data.timeFlexible ? t("request.flexible") : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
              </dd>
            </div>
            {finance ? (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-app-muted">
                  {t("request.priceExpectation")}
                </dt>
                <dd>
                  {t(`request.pricing.${data.pricingPreference}`)}
                  {data.fixedAmount
                    ? ` · ${formatMoney(data.fixedAmount, data.currency)} ${
                        data.fixedAmountTaxIncluded
                          ? t("request.inclVat")
                          : t("request.exclVat")
                      }`
                    : ""}
                </dd>
              </div>
            ) : null}
          </dl>
        </Panel>
      )}
      <AttachmentGallery request={data} canEdit={canEdit} />
      <div className="flex flex-wrap justify-end gap-2">
        {!closed && (canChange || (data.status === "draft" && canEdit)) ? (
          <Button
            variant="text"
            severity="secondary"
            onClick={() => setClosing("cancelled")}
          >
            {t("request.cancelRequest")}
          </Button>
        ) : null}
        {!closed && data.status !== "draft" && canChange ? (
          <Button
            variant="text"
            severity="danger"
            onClick={() => setClosing("declined")}
          >
            {t("request.decline")}
          </Button>
        ) : null}
        {data.offers.length === 0 &&
        data.invoices.length === 0 &&
        ((data.status === "draft" && data.createdById === me.data?.user.id) ||
          has("requests.delete")) ? (
          <Button
            variant="text"
            severity="danger"
            icon="pi pi-trash"
            loading={remove.isPending || undefined}
            onClick={() =>
              window.confirm(t("request.deleteConfirm")) && remove.mutate()
            }
          >
            {t("common.delete")}
          </Button>
        ) : null}
      </div>
      <Modal
        open={closing !== null}
        onClose={() => setClosing(null)}
        title={
          closing === "declined"
            ? t("request.declineTitle")
            : t("request.cancelTitle")
        }
        footer={
          <>
            <Button
              variant="text"
              severity="secondary"
              onClick={() => setClosing(null)}
            >
              {t("common.back")}
            </Button>
            <Button
              severity="danger"
              disabled={!reason.trim()}
              loading={transition.isPending || undefined}
              onClick={() =>
                transition.mutate({ status: closing!, reason: reason.trim() })
              }
            >
              {closing === "declined"
                ? t("request.decline")
                : t("request.cancelRequest")}
            </Button>
          </>
        }
      >
        <Field label={t("common.reason")} required>
          <Textarea
            value={reason}
            rows={3}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setReason(event.target.value)
            }
          />
        </Field>
        <MutationError error={transition.error} />
      </Modal>
    </DocumentPage>
  );
}
